import { Camera, Image, PieChart } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface VisualTestingCardProps {
  data: {
    totalScreenshots: number;
    issuesWithScreenshots: number;
    avgScreenshotsPerIssue: number;
  } | null | undefined;
  isLoading?: boolean;
}

export function VisualTestingCard({
  data,
  isLoading = false,
}: VisualTestingCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Hide if no data available
  if (!data || data.totalScreenshots === 0) {
    return null;
  }

  return (
    <Card className="hover-elevate" data-testid="visual-testing-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Visual Testing Metrics
        </CardTitle>
        <CardDescription>
          Screenshot capture and visual testing coverage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Image className="h-4 w-4" />
              <span>Total Screenshots</span>
            </div>
            <div className="text-2xl font-bold">{data.totalScreenshots}</div>
            <p className="text-xs text-muted-foreground">
              Captured by agents
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <PieChart className="h-4 w-4" />
              <span>Issues with Screenshots</span>
            </div>
            <div className="text-2xl font-bold">{data.issuesWithScreenshots}</div>
            <p className="text-xs text-muted-foreground">
              Have visual evidence
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Camera className="h-4 w-4" />
              <span>Avg per Issue</span>
            </div>
            <div className="text-2xl font-bold">{data.avgScreenshotsPerIssue}</div>
            <p className="text-xs text-muted-foreground">
              {data.avgScreenshotsPerIssue >= 2 ? (
                <span className="text-green-600">Good coverage</span>
              ) : data.avgScreenshotsPerIssue >= 1 ? (
                <span className="text-yellow-600">Moderate coverage</span>
              ) : (
                <span className="text-muted-foreground">Limited coverage</span>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
